/* eslint-disable @typescript-eslint/ban-ts-comment */
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { Input } from 'components/Input/Input'
import { ISelectValue } from 'constants/globalTypes'
import { getSingleCycle } from 'features/cycles/cyclesSlice'
import React from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { Flex, ItemsGrid, ThinSeparator } from 'styling/GlobalStyles'
import { ICycleSingleResponse } from 'types/api/taskCyclesApi.interface'
import { IPhaseState, ITaskCycleBindingConfigState } from 'types/operationPlans.interface'

type CycleTemplateTypeProps = {
    formKey: string | number
    addingPhase: IPhaseState<ITaskCycleBindingConfigState>
    setAddingPhase: React.Dispatch<React.SetStateAction<IPhaseState<ITaskCycleBindingConfigState>>>
}

export const CycleTemplateType = ({ formKey, addingPhase, setAddingPhase }: CycleTemplateTypeProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const bindingConfig = addingPhase.bindingConfig ?? ({} as ITaskCycleBindingConfigState)

    const parse = (data: any) => {
        return data ? data.data.map((item: any) => ({ label: item.name, value: item.id })) : []
    }

    const updateBindingConfig = (patch: Partial<ITaskCycleBindingConfigState>) => {
        setAddingPhase((prev) => ({
            ...prev,
            bindingConfig: { ...(prev.bindingConfig ?? {}), ...patch } as ITaskCycleBindingConfigState,
        }))
    }

    const handleCycleSelect = async (value: ISelectValue) => {
        const resp = await dispatch(getSingleCycle(value.value.toString()))
        if (resp.meta.requestStatus === 'fulfilled') {
            setAddingPhase((prev) => ({
                ...prev,
                taskCycleTemplate: { label: value.label, value: value.value },
                bindingConfig: {
                    startDate: null,
                    parallelismTarget: null,
                    department: null,
                    componentsData: (resp.payload as ICycleSingleResponse).components.map((component) => ({
                        taskCycleTemplateComponent: { label: component.name, value: component.id },
                        componentType: component.component,
                        value: null,
                    })),
                },
            }))
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateBindingConfig({ [e.target.name]: e.target.value } as Partial<ITaskCycleBindingConfigState>)
    }

    const handleComponentValue = (e: React.ChangeEvent<HTMLInputElement>, componentId: number | string) => {
        const config = addingPhase.bindingConfig
        if (!config?.componentsData) return
        const newValue = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber
        updateBindingConfig({
            componentsData: config.componentsData.map((c) =>
                c.taskCycleTemplateComponent?.value === componentId ? { ...c, value: newValue } : c,
            ),
        })
    }

    return <>
        <Flex column gap='1rem' >
            <HonestSelectAsyncInput
                key={`cycle-template-${formKey}`}
                keyProp={1}
                defaultValue={addingPhase.taskCycleTemplate?.label}
                link='task/cycle/template'
                labelText='Task Cycle Template'
                onChange={handleCycleSelect}
                name='taskCycleTemplate'
                placeholder='Search Task Cycles'
                parserFunction={parse}
                required
                invalid={!addingPhase.taskCycleTemplate?.value}
            />
            {addingPhase.taskCycleTemplate?.value && <>
                <Flex gap='1rem' >
                    <CalendarInput
                        key={`cycle-startDate-${formKey}`}
                        labelText='Start Date'
                        name='startDate'
                        defaultValue={bindingConfig.startDate ?? null}
                        onChange={(date) => updateBindingConfig({ startDate: date })}
                        width='100%'
                    />
                    <Input
                        width='100%'
                        value={bindingConfig.parallelismTarget ?? ''}
                        placeholder='Enter parallelism target'
                        labelText='Parallelism Target'
                        name='parallelismTarget'
                        onChange={handleChange}
                        nomarg
                        type='number'
                    />
                </Flex>
                <HonestSelectAsyncInput
                    key={`cycle-department-${formKey}`}
                    keyProp={1}
                    link='departments'
                    placeholder='Search department'
                    name='department'
                    labelText='Department'
                    defaultValue={bindingConfig.department?.label}
                    onChange={(value) => updateBindingConfig({ department: value })}
                    parserFunction={(data: any) =>
                        data.data.map((i: any) => ({ label: i.name, value: i.id }))
                    }
                />
                <ThinSeparator />
                <ItemsGrid columns={2} >
                    {bindingConfig.componentsData?.map((c) =>
                        <Input
                            value={c.value ?? ''}
                            key={c.taskCycleTemplateComponent?.value}
                            width='100%'
                            placeholder='Enter value'
                            labelText={c.taskCycleTemplateComponent.label + ' value'}
                            name='value'
                            onChange={(e) => handleComponentValue(e, c.taskCycleTemplateComponent?.value)}
                            type='number'
                        />,
                    )}
                </ItemsGrid>
            </>}
        </Flex>

    </>
}
